import { Tooltip } from '@mui/material';
import {
  getGridStringOperators,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import { type ReactNode } from 'react';

import LocalStorageService from '~/services/localStorage.service';

import { type FilterModel } from './filters';

type RowHeight = {
  THIN: number;
  VERY_THIN: number;
};

class DatagridUtils {
  readonly TABLE_HEIGHT_10_ROWS = '668px';
  readonly ROW_HEIGHT: RowHeight = {
    THIN: 35,
    VERY_THIN: 25,
  };

  readonly EMPTY_FILTER_MODEL: FilterModel = {
    items: [],
    logicOperator: 'and',
  };

  /**
   * Render a tooltip with the cell value on hover.
   * Used to make it easier to read if the cell is too narrow to display the full value.
   */
  displayCellTooltip(params: GridRenderCellParams) {
    let { value } = params;
    if (typeof value === 'boolean') {
      value = value ? 'Ja' : 'Nein';
    }

    return (
      <Tooltip title={value ?? ''}>
        <span>{value}</span>
      </Tooltip>
    );
  }

  displayCellTooltipControlled(renderValue: ReactNode, hoverValue?: string) {
    return (
      <Tooltip title={hoverValue ?? ''}>
        <span>{renderValue}</span>
      </Tooltip>
    );
  }

  customColumnTypeArray() {
    return {
      filterOperators: getGridStringOperators().map((operator) => {
        let getApplyFilterFunction = operator.getApplyFilterFn;

        switch (operator.value) {
          case 'contains': {
            getApplyFilterFunction = (filterItem) => (value) =>
              !filterItem.value ||
              value?.some((item: string | number) =>
                item
                  .toString()
                  .toLowerCase()
                  .includes(filterItem.value.toLowerCase()),
              );
            break;
          }

          case 'equals': {
            getApplyFilterFunction = (filterItem) => (value) =>
              !filterItem.value ||
              value?.some(
                (item: string | number) =>
                  item.toString().toLowerCase() ===
                  filterItem.value.toLowerCase(),
              );
            break;
          }

          case 'startsWith': {
            getApplyFilterFunction = (filterItem) => (value) =>
              !filterItem.value ||
              value?.some((item: string | number) =>
                item
                  .toString()
                  .toLowerCase()
                  .startsWith(filterItem.value.toLowerCase()),
              );
            break;
          }

          case 'endsWith': {
            getApplyFilterFunction = (filterItem) => (value) =>
              !filterItem.value ||
              value?.some((item: string | number) =>
                item
                  .toString()
                  .toLowerCase()
                  .endsWith(filterItem.value.toLowerCase()),
              );
            break;
          }

          case 'isEmpty': {
            getApplyFilterFunction = () => (value) => value?.length === 0;
            break;
          }

          case 'isNotEmpty': {
            getApplyFilterFunction = () => (value) => value?.length > 0;
            break;
          }

          case 'isAnyOf': {
            getApplyFilterFunction = (filterItem) => (value) =>
              !filterItem.value ||
              value?.some((item: string | number) =>
                filterItem.value?.find(
                  (searchItem: string) =>
                    searchItem.toLowerCase() === item.toString().toLowerCase(),
                ),
              );
            break;
          }

          default: {
            break;
          }
        }

        return {
          ...operator,
          getApplyFilterFn: getApplyFilterFunction,
        };
      }),
      renderCell: (params: GridRenderCellParams) =>
        this.displayCellTooltipControlled(
          params.value.join(', '),
          params.value.join(', '),
        ),
      sortComparator: (v1: string[], v2: string[]) =>
        v1.join(', ').localeCompare(v2.join(', ')),
    };
  }

  getCookieRowHeight(cookieName: string) {
    const cookie = LocalStorageService.getObjectFromLocalStorage(cookieName);

    if (!cookie?.rowHeight) {
      return this.ROW_HEIGHT.THIN;
    } // default data grid row height

    return Number(cookie?.rowHeight);
  }

  resizeText(
    rowHeight: number,
    renderFunction: (params: GridRenderCellParams) => ReactNode,
  ) {
    const fontClass = rowHeight <= this.ROW_HEIGHT.THIN ? 'text-sm' : '';

    return {
      renderCell: (params: GridRenderCellParams) => (
        <span className={fontClass}>
          {renderFunction
            ? renderFunction(params)
            : this.displayCellTooltip(params)}
        </span>
      ),
    };
  }

  getColumnWidthFactor(rowHeight: number) {
    return [this.ROW_HEIGHT.VERY_THIN, this.ROW_HEIGHT.THIN].includes(rowHeight)
      ? 0.8
      : 1;
  }
}

const datagridUtilsInstance = new DatagridUtils();

export default datagridUtilsInstance;
